/**
 * MatchDayModeSection - Settings toggle for Match Day Mode
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Timer, AlertCircle, Mic } from 'lucide-react';

export const MatchDayModeSection = ({
  matchDayModeEnabled,
  setMatchDayModeEnabled,
  voiceButtonPosition,
  setVoiceButtonPosition,
  voiceControlEnabled,
  setVoiceControlEnabled,
  voiceEngine,
  setVoiceEngine,
}) => {
  return (
    <Card data-testid="match-day-mode-settings-card">
      <CardHeader>
        <CardTitle className="text-xl font-semibold font-title flex items-center gap-2">
          <Timer className="w-5 h-5 text-emerald-600" strokeWidth={1.5} />
          Match Day Mode
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
              <Timer className="w-5 h-5" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <Label htmlFor="match-day-mode-toggle" className="text-base font-medium text-gray-900 cursor-pointer">
                Enable Match Day Mode
              </Label>
              <p className="text-sm text-gray-500 mt-1">
                A streamlined coaching interface for use on match days. Quick event logging, live score updates, and rapid substitution management from the touchline.
              </p>
            </div>
          </div>
          <Switch
            id="match-day-mode-toggle"
            checked={matchDayModeEnabled}
            onCheckedChange={setMatchDayModeEnabled}
            className="data-[state=checked]:bg-emerald-600"
            data-testid="match-day-mode-toggle"
          />
        </div>

        {matchDayModeEnabled && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <AlertCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-emerald-800">
                Match Day Mode is active
              </p>
              <p className="text-sm text-emerald-700 mt-1">
                On match days, a "Match Day Mode" button will appear on the dashboard and match edit pages, 
                giving you quick access to the streamlined coaching interface.
              </p>
            </div>
          </div>
        )}

        {matchDayModeEnabled && (
          <div className="pt-2 border-t border-gray-100 space-y-5">
            {/* Voice Control Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-gray-50 border border-gray-200">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
                  <Mic className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <Label htmlFor="voice-control-toggle" className="text-base font-medium text-gray-900 cursor-pointer">
                    Voice Control
                  </Label>
                  <p className="text-sm text-gray-500 mt-1">
                    Enable the floating voice button during Match Day Mode for hands-free event logging.
                  </p>
                </div>
              </div>
              <Switch
                id="voice-control-toggle"
                checked={voiceControlEnabled}
                onCheckedChange={setVoiceControlEnabled}
                className="data-[state=checked]:bg-emerald-600"
                data-testid="voice-control-toggle"
              />
            </div>

            {/* Voice Button Position */}
            {voiceControlEnabled && (
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">Voice Button Position</Label>
                <p className="text-xs text-gray-400 mb-3">Choose which side the floating voice button appears on during Match Day Mode.</p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setVoiceButtonPosition('left')}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                  voiceButtonPosition === 'left'
                    ? 'bg-emerald-600 text-white ring-1 ring-emerald-400'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="voice-btn-pos-left"
              >
                Bottom Left
              </button>
              <button
                type="button"
                onClick={() => setVoiceButtonPosition('right')}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                  voiceButtonPosition === 'right'
                    ? 'bg-emerald-600 text-white ring-1 ring-emerald-400'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="voice-btn-pos-right"
              >
                Bottom Right
              </button>
            </div>
              </div>
            )}

            {/* Voice Engine */}
            {voiceControlEnabled && (
              <div>
                <Label className="text-sm font-medium text-gray-700 mb-3 block">Voice Engine</Label>
                <p className="text-xs text-gray-400 mb-3">
                  <span className="font-semibold text-gray-600">Browser</span> uses your device's built-in speech recognition (offline, instant).
                  {' '}
                  <span className="font-semibold text-gray-600">Whisper</span> uploads a short audio clip to OpenAI for higher accuracy with accents and crowd noise.
                </p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setVoiceEngine('webspeech')}
                    className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                      voiceEngine === 'webspeech'
                        ? 'bg-emerald-600 text-white ring-1 ring-emerald-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="voice-engine-webspeech"
                  >
                    Browser (default)
                  </button>
                  <button
                    type="button"
                    onClick={() => setVoiceEngine('whisper')}
                    className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
                      voiceEngine === 'whisper'
                        ? 'bg-emerald-600 text-white ring-1 ring-emerald-400'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid="voice-engine-whisper"
                  >
                    OpenAI Whisper
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MatchDayModeSection;
